import React, { ReactNode, useCallback, useState } from 'react';
import { ModalDispatchContext } from './ModalContext';

interface ModalProviderProps {
  children: ReactNode;
}

interface ModalState {
  modal: React.ReactElement | null;
  isOpen: boolean;
  key: string;
}

function ModalProvider({ children }: ModalProviderProps) {
  const [modalState, setModalState] = useState<ModalState>({
    modal: null,
    isOpen: false,
    key: '',
  });

  const dispatchModal = useCallback((modal: React.ReactElement | null) => {
    if (!modal) {
      // Close first so the modal component can complete teardown, including body unlock.
      setModalState((currentState) => ({
        ...currentState,
        isOpen: false,
      }));
      return;
    }

    setModalState((currentState) => {
      const currentModalId = (currentState.modal?.props as { modalId?: string } | undefined)?.modalId;
      const nextModalId = (modal.props as { modalId?: string } | undefined)?.modalId;

      // Reuse the key when re-dispatching the same open modal so its content updates
      // in place instead of remounting (a remount causes a visible flash/blink).
      const key =
        currentState.isOpen && currentModalId && nextModalId && currentModalId === nextModalId
          ? currentState.key
          : crypto.randomUUID();

      return {
        modal,
        isOpen: true,
        key,
      };
    });
  }, []);

  const { modal: currentModal, isOpen, key } = modalState;

  const renderedModal =
    currentModal && React.isValidElement(currentModal)
      ? // The provider is the single source of truth for modal visibility.
        // We inject `open` so close lifecycle hooks run before any future unmount.
        React.cloneElement(currentModal as React.ReactElement<Record<string, unknown>>, {
          open: isOpen,
          key,
        })
      : currentModal;

  return (
    <ModalDispatchContext.Provider key='modal-wrapper' value={dispatchModal}>
      {children}
      {renderedModal}
    </ModalDispatchContext.Provider>
  );
}

export default ModalProvider;
